<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import {
  iAmountRangeFilter,
  iBadge,
  iBadgeColours,
  iButton,
  iFilter,
  iPagination,
  iSelect,
  iSearchInput,
  iTable,
  iTableRow,
  iTableColumnsFilter,
  iTableSortButton,
  showToast,
} from '@inscribe/signal';
import { toCurrency } from '@/utils/toCurrency';
import { getAndSyncQueryParams, updateQueryParams } from '@/utils/queryParams';
import DatesFilterWithCustomRange from '@/V2/components/filters/DatesFilterWithCustomRange';
import CreditTransactionRecategorisationMenu from '@/V2/credit/components/CreditTransactionRecategorisationMenu';
import CreditBulkRecategorisationPopup from '@/V2/credit/components/CreditBulkRecategorisationPopup';
import EmptyPage from '@/V2/components/EmptyPage';
import InfoIcon from '@/V2/components/InfoIcon';
import { writeStorage, readStorage } from '@/utils/storage';
import { normalizeText } from '@/V2/credit/granular-insights/components/components.utils';
import { $event } from '@/bus';
import { formatDateTime } from '@/utils/date';
import { getUseAllDocsQueryParameter } from '@/V2/credit/utils';
import creditTransactionsCategories from './CreditTransactionsCategories.content';

const DEFAULT_PER_PAGE_OPTIONS = [
  { value: '20', text: '20' },
  { value: '50', text: '50' },
  { value: '100', text: '100' },
];

const DEFAULT_FILTER_PARAMS = {
  size: 20,
  sort_by: 'date',
  sort_direction: 'asc',
  page: 1,
  search: '',
  start_date: '',
  end_date: '',
  data_source: '',
};

const SELECTED_COLUMNS_STORAGE_KEY = 'transactionsTableColumns';

export default {
  name: 'CreditTransactions',
  components: {
    DatesFilterWithCustomRange,
    EmptyPage,
    iAmountRangeFilter,
    iBadge,
    iButton,
    iFilter,
    iPagination,
    iSelect,
    iSearchInput,
    iTable,
    iTableRow,
    iTableColumnsFilter,
    iTableSortButton,
    InfoIcon,
    CreditTransactionRecategorisationMenu,
    CreditBulkRecategorisationPopup,
  },
  props: {
    tableTitle: {
      type: String,
      default: 'Transactions',
    },
    categoryFilter: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      iBadgeColours,
      loading: true,
      selectAll: false,
      totalTransactions: undefined,
      perPageOptions: [...DEFAULT_PER_PAGE_OPTIONS],
      selectedItems: [],
      filterParams: {
        ...DEFAULT_FILTER_PARAMS,
        ...getAndSyncQueryParams({
          fullPath: this.$route.fullPath,
          router: this.$router,
          store: this.$store,
        }),
      },
    };
  },
  computed: {
    ...mapState({
      credit: 'credit',
    }),
    ...mapGetters([
      'creditBulkRecategorisationEnabled',
      'creditTransactionsSearchFiltersEnabled',
      'isOrgReadOnlyUser',
    ]),

    isTransactionSelected() {
      return (transaction) => {
        return this.selectedItems.includes(transaction);
      };
    },
    showPagination() {
      return this.totalTransactions > this.filterParams.size && !this.loading;
    },
    hasTransactions() {
      return this.credit.transactions?.data?.length > 0;
    },
    entriesText() {
      const noun = this.totalTransactions === 1 ? 'Entry' : 'Entries';
      return this.hasTransactions ? `${this.totalTransactions} ${noun}` : '---';
    },
    hasSelectedItems() {
      return this.selectedItems.length > 0;
    },
    hasSelectedFilters() {
      return !!(
        (this.filterParams.start_date && this.filterParams.end_date) ||
        this.filterParams.data_source ||
        (this.filterParams.min_amount && this.filterParams.max_amount) ||
        this.filterParams?.categories?.length
      );
    },
    dataSourceOptions() {
      return [
        {
          label: 'Bank Statement',
          value: 'bank_statement',
        },
        {
          label: 'Open Banking',
          value: 'open_banking',
        },
      ];
    },
    useAllDocsQueryParameter() {
      return getUseAllDocsQueryParameter(this.filterParams);
    },
    filterCatgories() {
      return Object.keys(creditTransactionsCategories).map((categoryKey) => {
        return {
          value: categoryKey,
          label: creditTransactionsCategories[categoryKey].label,
        };
      });
    },
    tableSelectable() {
      return (
        this.creditBulkRecategorisationEnabled &&
        this.hasTransactions &&
        !this.isOrgReadOnlyUser
      );
    },
  },
  watch: {
    selectAll: {
      handler: function (value) {
        if (value) {
          this.selectAllRows();
        } else {
          this.clearSelected();
        }
      },
      immediate: true,
    },
    '$route.fullPath': {
      handler: function (newFullPath) {
        const oldUseAllDocsValue = this.params?.use_all_docs;
        this.params = getAndSyncQueryParams({
          fullPath: newFullPath,
          router: this.$router,
          store: this.$store,
        });
        if (oldUseAllDocsValue !== this.params?.use_all_docs) {
          this.updateAllData();
        }
      },
    },
  },
  created() {
    $event.$on('credit:refresh', () => {
      this.getCustomerTransactionsCategoriesList();
      this.updateAllData();
    });
  },
  async mounted() {
    await this.getCustomerTransactionsCategoriesList();
    await this.updateAllData();
  },
  methods: {
    ...mapActions([
      'getCustomerTransactions',
      'getCustomerTransactionsExportsCsv',
      'getCustomerTransactionsCategoriesList',
    ]),

    manageTableSelectedColumns() {
      const storedColumns = readStorage(SELECTED_COLUMNS_STORAGE_KEY);
      if (storedColumns && this.$refs.transactionsTable) {
        this.$refs.transactionsTable.setVisibleColumns(storedColumns);
      }
      this.loading = false;
    },

    onOrderChange(field, order) {
      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          sort_by: field,
          sort_direction: order,
          use_all_docs: !this.filterParams.use_all_docs,
        },
      });
    },
    formatDateTime,
    async updateAllData() {
      this.selectAll = false;
      await this.fetchTransactions();
    },

    // This is the main method used in this component, generally, any changes to filtering/sorting/pagination/searching will trigger a call to this method
    async fetchTransactions(page = this.filterParams.page) {
      // sync query params with filterParams
      this.filterParams = {
        ...this.filterParams,
        ...getAndSyncQueryParams({
          fullPath: this.$route.fullPath,
          router: this.$router,
          store: this.$store,
        }),
      };

      // Set the page to the page number passed in
      this.filterParams.page = page;
      // Set loading to true to show the spinner
      this.loading = true;

      // deselect all rows if selectAll is true
      if (this.selectAll) {
        this.selectAll = false;
      }

      const paramsArray = [
        `sort_by=${this.filterParams.sort_by}`,
        `sort_direction=${this.filterParams.sort_direction}`,
        `size=${this.filterParams.size}`,
        `page=${this.filterParams.page}`,
        `include_unreconciled_documents=${this.useAllDocsQueryParameter}`,
      ];

      // Add category to paramsArray if categoryFilter is present // category filter from props
      if (this.categoryFilter) {
        paramsArray.push(`category=${this.categoryFilter}`);

        updateQueryParams({
          fullPath: this.$route.fullPath,
          router: this.$router,
          store: this.$store,
          updates: {
            category: this.categoryFilter,
          },
        });
      }

      // category filter from i-filter
      if (this.filterParams?.categories) {
        paramsArray.push(`category=${this.filterParams.categories}`);
      }

      // Add search parameter to paramsArray if searchText is longer than 2 characters
      if (this.filterParams?.search?.length > 2) {
        paramsArray.push(`search=${this.filterParams.search}`);
      }

      if (this.filterParams.start_date && this.filterParams.end_date) {
        paramsArray.push(`start_date=${this.filterParams.start_date}`);
        paramsArray.push(`end_date=${this.filterParams.end_date}`);
      }

      if (this.filterParams.data_source) {
        paramsArray.push(`data_source=${this.filterParams.data_source}`);
      }

      if (this.filterParams.min_amount && this.filterParams.max_amount) {
        // this just converts the dollar amounts to cents for the API
        paramsArray.push(`min_amount=${this.filterParams.min_amount * 100}`);
        paramsArray.push(`max_amount=${this.filterParams.max_amount * 100}`);
      }

      // Join the paramsArray with '&' to create the final params string
      const paramsString = paramsArray.join('&');

      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          page: this.filterParams.page,
          size: this.filterParams.size,
          sort_direction: this.filterParams.sort_direction,
          sort_by: this.filterParams.sort_by,
        },
      });

      this.getCustomerTransactions({
        customerId: this.$route.params.customer_id,
        params: paramsString,
      })
        .then((res) => {
          this.totalTransactions = this.credit.transactions
            ? this.credit.transactions.pagination.total
            : null;
        })
        .catch((err) => {
          showToast({
            text:
              err?.response?.data?.message
                ?.replace(/.*\[(.*)+\].*/g, '$1')
                .replace(/'/g, '')
                .replace(/"/g, '') ||
              "We couldn't complete the search now. Please try again later.",
            isError: true,
          });
        })
        .finally(() => {
          this.$nextTick(() => {
            this.manageTableSelectedColumns();
          });
        });
    },
    onRowSelected(items) {
      this.selectedItems = items;
    },
    onCategoriesUpdated() {
      setTimeout(this.clearSelected);
    },
    clearSelected() {
      this.selectedItems.splice(0);
    },
    toggleRowSelection(indexes) {
      this.selectedItems = this.credit.transactions.data.filter((item, index) =>
        indexes.includes(index),
      );
    },
    viewMoreText(number) {
      return `${number}`;
    },
    getDisplayValue(value) {
      return value ? normalizeText(value) : this.displayEmptyPlaceholder();
    },
    getDisplayValueForMerchants(value) {
      return value || this.displayEmptyPlaceholder();
    },
    displayEmptyPlaceholder() {
      return '---';
    },
    onPerPageChange(value) {
      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          size: Number(value),
          use_all_docs: !this.filterParams.use_all_docs,
        },
      });
    },
    onSearchInput(value) {
      this.filterParams.search = value.length > 2 ? value : '';

      // Fetch transactions only if searchText is valid or empty
      if (value.length > 2 || value.length === 0) {
        updateQueryParams({
          fullPath: this.$route.fullPath,
          router: this.$router,
          store: this.$store,
          updates: {
            search: this.filterParams.search,
          },
        });
        this.fetchTransactions(1);
      }
    },
    onDataSourceChange(updated) {
      if (updated.selection.length > 0) {
        this.filterParams.data_source = updated.selection[0].value;
      } else {
        this.filterParams.data_source = '';
      }

      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          data_source: this.filterParams.data_source,
        },
      });
      this.fetchTransactions(1);
    },
    onDatesFilterChange(dateRange) {
      this.filterParams.start_date = dateRange.startDate;
      this.filterParams.end_date = dateRange.endDate;

      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
        },
      });
      this.fetchTransactions(1);
    },
    onAmountFilterChange(amountRange) {
      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          min_amount: amountRange.minAmount ? amountRange.minAmount : '',
          max_amount: amountRange.maxAmount ? amountRange.maxAmount : '',
          use_all_docs: !this.filterParams.use_all_docs,
        },
      });
    },
    onCategoryFilterChanged(value) {
      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          categories: value.selection?.map((v) => v.value) || [],
          use_all_docs: !this.filterParams.use_all_docs,
        },
      });
    },
    getDataSourceLabelByValue(value) {
      const option = this.dataSourceOptions.find(
        (option) => option.value === value,
      );
      return option ? option.label : null;
    },
    getCategoryLabelByValue(value) {
      return creditTransactionsCategories[value]?.label || '';
    },
    clearFilters() {
      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          categories: [],
          start_date: '',
          end_date: '',
          data_source: '',
          min_amount: '',
          max_amount: '',
          use_all_docs: !this.filterParams.use_all_docs,
        },
      });
    },
    valueToCurrency: toCurrency,
    onColumnsUpdated(columns) {
      writeStorage(SELECTED_COLUMNS_STORAGE_KEY, columns);
    },
  },
};
</script>

<template>
  <div
    id="credit-transactions"
    :class="{ loading }"
    class="credit-transactions d-flex flex-column i-gap-10"
  >
    <!-- header -->
    <div class="transactions-header d-flex flex-column align-items-start">
      <div class="d-flex flex-column i-gap-2">
        <!-- main line-->
        <div class="title d-flex flex-row">
          <h2 class="i-text-heading-4 i-text-neutral-120 i-m-0">
            {{ tableTitle }}
          </h2>

          <i-badge
            v-if="totalTransactions"
            class="i-ml-4"
            :colour="iBadgeColours.PRIMARY"
            :label="entriesText"
            alt-text-colour
          />
        </div>

        <!-- subtitle -->
        <div class="i-text-body-2 i-text-neutral-110">
          View transactions for this customer
        </div>
      </div>
    </div>

    <div
      class="credit-transactions__filters d-flex flex-column align-items-start i-gap-6"
    >
      <div
        v-if="creditTransactionsSearchFiltersEnabled"
        class="search-wrapper d-flex i-gap-6"
      >
        <i-search-input
          :filled-value="filterParams.search"
          class="search-input"
          data-test-id="transaction-search-input"
          data-dd-action-name="transaction-search-input"
          :debounce-time="500"
          input-placeholder="Search transaction descriptions"
          @input="onSearchInput"
        ></i-search-input>
      </div>

      <div class="d-flex i-gap-3">
        <!-- date range -->
        <dates-filter-with-custom-range
          :params="filterParams"
          @dates-filter-updated="onDatesFilterChange"
        ></dates-filter-with-custom-range>

        <!-- data source -->
        <i-filter
          label="Data Source"
          :options="dataSourceOptions"
          :max-selectable="1"
          :searchable="false"
          :value="[getDataSourceLabelByValue(filterParams.data_source)]"
          @selection-changed="onDataSourceChange"
        />

        <!-- amount range filter -->
        <i-amount-range-filter
          :amounts="{
            min: filterParams.min_amount ? filterParams.min_amount : undefined,
            max: filterParams.min_amount ? filterParams.max_amount : undefined,
          }"
          @amount-changed="onAmountFilterChange"
        />

        <!-- categories filter -->
        <i-filter
          label="Category"
          :options="filterCatgories"
          :max-selectable="1"
          :searchable="false"
          :value="[getCategoryLabelByValue(filterParams.categories)]"
          @selection-changed="onCategoryFilterChanged"
        />
      </div>

      <i-button
        v-if="hasSelectedFilters"
        class="i-button neutral outline sm light clear-filters"
        text="Clear filters"
        type="neutral"
        subtype="outline"
        size="sm"
        @button-click="clearFilters"
      >
      </i-button>
    </div>

    <!-- empty state-->
    <empty-page
      v-if="!hasTransactions && !loading"
      class="d-flex i-mb-12"
      icon="large-file-text-search"
      title="No transactions available"
      data-testid="transactions-empty-page"
      subtitle="Transactions will appear here when they are present in documents"
    ></empty-page>

    <!-- has transactions -->
    <div v-else class="i-pb-12">
      <i-table
        data-testid="transactions-table"
        :selectable="tableSelectable"
        :loading="loading"
        table-id="transactions-table"
        ref="transactionsTable"
        @selection-changed="toggleRowSelection"
      >
        <!-- table header -->
        <template #header>
          <i-table-row type="header" :selectable="tableSelectable">
            <template #cells>
              <div class="amount-cell not-hideable">
                <i-table-sort-button
                  label="Amount"
                  text-alignment="right"
                  @order-change="(order) => onOrderChange('amount', order)"
                ></i-table-sort-button>
              </div>
              <div class="description-cell not-hideable">Description</div>
              <i-table-sort-button
                label="Category"
                @order-change="(order) => onOrderChange('category', order)"
              ></i-table-sort-button>
              <div class="method hide-less-1200">Method</div>
              <i-table-sort-button
                label="Type"
                @order-change="(order) => onOrderChange('merchant', order)"
              ></i-table-sort-button>
              <div class="source hide-less-1200">
                <div class="d-flex flex-row align-items-center">
                  <span class="i-mr-4">Source</span>
                  <info-icon
                    class="i-mr-4"
                    title="Source"
                    description="Indicates the origin of the data, such as bank statements or open banking data."
                  />
                </div>
              </div>
              <i-table-sort-button
                label="Date"
                @order-change="(order) => onOrderChange('date', order)"
              ></i-table-sort-button>
            </template>
          </i-table-row>
        </template>

        <!-- table body -->
        <template #rows>
          <i-table-row
            v-for="transaction in hasTransactions
              ? credit.transactions.data
              : []"
            :key="transaction.id"
            :selected="isTransactionSelected(transaction)"
          >
            <template #cells>
              <div class="amount-cell align-right centered not-hideable">
                {{
                  valueToCurrency(
                    transaction.amount,
                    transaction.currency_code,
                    2,
                  )
                }}
              </div>
              <div
                :class="{ centered: transaction.description.length < 50 }"
                class="full-content not-hideable"
              >
                {{ transaction.description }}
              </div>
              <div>
                <credit-transaction-recategorisation-menu
                  ref="recategorisationMenu"
                  :is-disabled="!hasTransactions || isOrgReadOnlyUser"
                  :transaction-category="transaction.category"
                  :transaction-ids="[transaction.id]"
                />
              </div>
              <div class="method centered hide-less-1500">
                {{ getDisplayValue(transaction.method) }}
              </div>
              <div class="centered">
                {{ getDisplayValueForMerchants(transaction.merchant) }}
              </div>
              <div class="capitalize source centered hide-less-1200">
                {{ getDisplayValue(transaction.data_source) }}
              </div>
              <div class="centered">
                {{
                  transaction.date !== '-'
                    ? formatDateTime(transaction.date, 'DD MMM YYYY')
                    : transaction.date
                }}
              </div>
            </template>
          </i-table-row>
        </template>
      </i-table>

      <div class="pagination d-flex justify-content-between i-mt-10">
        <div>
          <i-pagination
            v-if="showPagination"
            size="md"
            :total-items="totalTransactions"
            :items-per-page="Number(filterParams.size)"
            :selected-page="Number(filterParams.page)"
            @page-click="fetchTransactions"
          ></i-pagination>
        </div>

        <div class="d-flex flex-row align-items-center i-gap-6">
          <i-table-columns-filter
            table-id="transactions-table"
            @columns-selection-changed="onColumnsUpdated"
          ></i-table-columns-filter>

          <i-select
            data-testid="per-page-select"
            :options="perPageOptions"
            :default-value="filterParams.size.toString()"
            placeholder="Items per page"
            @option-selected="onPerPageChange"
          ></i-select>
        </div>
      </div>

      <credit-bulk-recategorisation-popup
        v-if="creditBulkRecategorisationEnabled && hasSelectedItems"
        :selected-transactions="selectedItems"
        @categories-updated="onCategoriesUpdated"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.credit-transactions {
  position: relative;

  &__filters {
    .clear-filters {
      padding: 8px 12px;
      font-size: map.get($text-size-standard, 3);
      font-weight: map.get($text-weights, 'medium') !important;
      border-radius: 16px;
    }

    .search-wrapper {
      width: 100%;

      .search-input {
        width: 100%;
        height: 42px;
      }
    }
  }

  .i-table-row {
    height: 44px;
    .amount-cell {
      font-weight: 500 !important;
      justify-content: end;
    }
  }

  .capitalize {
    &::first-letter {
      text-transform: uppercase;
    }
  }

  .pagination {
    margin-bottom: 0;
    width: 100%;

    .i-select {
      width: 85px;

      :deep(.selected-input) {
        padding: 8px 16px;
      }
    }
  }

  :deep(.i-select) {
    width: 150px;
    .items {
      bottom: 48px !important;
    }
  }
}
</style>
